import {
    Alert,
    AlertIcon,
    Box,
    Button,
    Divider,
    HStack,
    IconButton,
    Input,
    Select,
    SimpleGrid,
    Spinner,
    Stat,
    StatLabel,
    StatNumber,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    VStack,
    useToast,
} from "@chakra-ui/react";
import { RepeatIcon, TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import { useCallback, useEffect, useMemo, useState } from "react";

const createDefaultFilters = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 6);

    const toInputValue = (date) => {
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, "0");
        const day = `${date.getDate()}`.padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    return {
        startDate: toInputValue(start),
        endDate: toInputValue(end),
        userId: "",
        view: "grouped",
    };
};

const EMPTY_SUMMARY = {
    totalSessions: 0,
    totalUsers: 0,
    totalDurationMs: 0,
    averageDurationMs: 0,
};

const formatDateTime = (value) => {
    if (!value) return "—";
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString("pt-BR", {
        timeZone: "America/Sao_Paulo",
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatDuration = (durationMs) => {
    if (!durationMs || durationMs <= 0) return "< 1 min";

    const totalMinutes = Math.round(durationMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours === 0) return `${minutes} min`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}min`;
};

const toTimestamp = (value) => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.getTime();
};

const compareSortValues = (left, right) => {
    if (left == null && right == null) return 0;
    if (left == null) return 1;
    if (right == null) return -1;

    if (typeof left === "string" || typeof right === "string") {
        return String(left).localeCompare(String(right), "pt-BR", {
            numeric: true,
            sensitivity: "base",
        });
    }

    if (left > right) return 1;
    if (left < right) return -1;
    return 0;
};

const escapeHtml = (value) => {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;")
        .replace(/'/g, "&#39;");
};

const AccessReportTab = () => {
    const [users, setUsers] = useState([]);
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(EMPTY_SUMMARY);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState(createDefaultFilters);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const toast = useToast();

    const groupedColumns = useMemo(() => ([
        {
            key: "username",
            label: "Usuário",
            render: (row) => row.username || "—",
            sortValue: (row) => row.username || "",
            defaultDirection: "asc",
        },
        {
            key: "sessionsCount",
            label: "Sessões",
            render: (row) => row.sessionsCount,
            sortValue: (row) => Number(row.sessionsCount ?? 0),
            defaultDirection: "desc",
        },
        {
            key: "firstLoginAt",
            label: "Primeira entrada",
            render: (row) => formatDateTime(row.firstLoginAt),
            sortValue: (row) => toTimestamp(row.firstLoginAt),
            defaultDirection: "desc",
        },
        {
            key: "lastAuditAt",
            label: "Última atividade",
            render: (row) => formatDateTime(row.lastAuditAt),
            sortValue: (row) => toTimestamp(row.lastAuditAt),
            defaultDirection: "desc",
        },
        {
            key: "totalDurationMs",
            label: "Tempo total",
            render: (row) => formatDuration(row.totalDurationMs),
            sortValue: (row) => Number(row.totalDurationMs ?? 0),
            defaultDirection: "desc",
        },
        {
            key: "averageDurationMs",
            label: "Tempo médio",
            render: (row) => formatDuration(row.averageDurationMs),
            sortValue: (row) => Number(row.averageDurationMs ?? 0),
            defaultDirection: "desc",
        },
        {
            key: "lastAction",
            label: "Última requisição",
            render: (row) => row.lastAction || "—",
            sortValue: (row) => row.lastAction || "",
            defaultDirection: "asc",
        },
    ]), []);

    const individualColumns = useMemo(() => ([
        {
            key: "username",
            label: "Usuário",
            render: (row) => row.username || "—",
            sortValue: (row) => row.username || "",
            defaultDirection: "asc",
        },
        {
            key: "loginAt",
            label: "Entrada",
            render: (row) => formatDateTime(row.loginAt),
            sortValue: (row) => toTimestamp(row.loginAt),
            defaultDirection: "desc",
        },
        {
            key: "lastAuditAt",
            label: "Última atividade",
            render: (row) => formatDateTime(row.lastAuditAt),
            sortValue: (row) => toTimestamp(row.lastAuditAt),
            defaultDirection: "desc",
        },
        {
            key: "durationMs",
            label: "Tempo na sessão",
            render: (row) => formatDuration(row.durationMs),
            sortValue: (row) => Number(row.durationMs ?? 0),
            defaultDirection: "desc",
        },
        {
            key: "lastAction",
            label: "Última requisição",
            render: (row) => row.lastAction || "—",
            sortValue: (row) => row.lastAction || "",
            defaultDirection: "asc",
        },
    ]), []);

    const activeColumns = filters.view === "grouped" ? groupedColumns : individualColumns;

    const sortedRows = useMemo(() => {
        if (!sortConfig.key) return rows;

        const activeColumn = activeColumns.find((column) => column.key === sortConfig.key);
        if (!activeColumn) return rows;

        return [...rows].sort((left, right) => {
            const comparison = compareSortValues(
                activeColumn.sortValue(left),
                activeColumn.sortValue(right)
            );

            return sortConfig.direction === "asc" ? comparison : -comparison;
        });
    }, [activeColumns, rows, sortConfig]);

    const fetchUsers = useCallback(async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch("/api/users", {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (!response.ok) throw new Error("Erro ao carregar usuários");

            const data = await response.json();
            setUsers(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Erro ao carregar usuários do relatório:", error);
            toast({
                title: "Erro ao carregar usuários do relatório",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    }, [toast]);

    const fetchReport = useCallback(async (currentFilters = filters) => {
        if (currentFilters.view === "individual" && !currentFilters.userId) {
            setRows([]);
            setSummary({ ...EMPTY_SUMMARY });
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const params = new URLSearchParams({ view: currentFilters.view });

            if (currentFilters.startDate) params.set("startDate", currentFilters.startDate);
            if (currentFilters.endDate) params.set("endDate", currentFilters.endDate);
            if (currentFilters.userId) params.set("userId", currentFilters.userId);

            const response = await fetch(`/api/access-logs/report?${params}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (!response.ok) throw new Error("Erro ao gerar relatório");

            const data = await response.json();
            setRows(Array.isArray(data.rows) ? data.rows : []);
            setSummary(data.summary || { ...EMPTY_SUMMARY });
        } catch (error) {
            console.error("Erro ao gerar relatório de acesso:", error);
            setRows([]);
            setSummary({ ...EMPTY_SUMMARY });
            toast({
                title: "Erro ao gerar relatório de acesso",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    }, [filters, toast]);

    useEffect(() => {
        fetchUsers();
        fetchReport(createDefaultFilters());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (filters.view === "individual" && !filters.userId) {
            setRows([]);
            setSummary({ ...EMPTY_SUMMARY });
        }
    }, [filters.userId, filters.view]);

    useEffect(() => {
        if (sortConfig.key && !activeColumns.some((column) => column.key === sortConfig.key)) {
            setSortConfig({ key: null, direction: "asc" });
        }
    }, [activeColumns, sortConfig.key]);

    const handleSort = (columnKey) => {
        const activeColumn = activeColumns.find((column) => column.key === columnKey);
        if (!activeColumn) return;

        setSortConfig((current) => {
            if (current.key === columnKey) {
                return {
                    key: columnKey,
                    direction: current.direction === "asc" ? "desc" : "asc",
                };
            }

            return {
                key: columnKey,
                direction: activeColumn.defaultDirection || "asc",
            };
        });
    };

    const handleGenerateReport = () => {
        if (filters.view === "individual" && !filters.userId) {
            toast({
                title: "Selecione um usuário",
                description: "O modo individual exige escolher um usuário específico.",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        fetchReport(filters);
    };

    const handleReset = () => {
        const nextFilters = createDefaultFilters();
        setFilters(nextFilters);
        setSortConfig({ key: null, direction: "asc" });
        fetchReport(nextFilters);
    };

    const handlePrint = () => {
        if (sortedRows.length === 0) {
            toast({
                title: "Nada para imprimir",
                description: "Gere um relatório com resultados antes de imprimir.",
                status: "info",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        const rowsHtml = sortedRows.map((row) => `
            <tr>
                ${activeColumns.map((column) => `<td>${escapeHtml(column.render(row))}</td>`).join("")}
            </tr>
        `).join("");

        const printWindow = window.open("", "_blank", "width=1200,height=800");
        if (!printWindow) {
            toast({
                title: "Não foi possível abrir a impressão",
                description: "Verifique se o navegador bloqueou a janela de impressão.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        printWindow.document.write(`
            <!DOCTYPE html>
            <html lang="pt-BR">
            <head>
                <meta charset="UTF-8" />
                <title>Relatório de acesso</title>
                <style>
                    body { font-family: Arial, sans-serif; padding: 24px; color: #1f2937; }
                    h1 { margin: 0 0 8px; font-size: 24px; }
                    p { margin: 4px 0; color: #4b5563; }
                    .summary { margin: 20px 0; display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
                    .card { border: 1px solid #d1d5db; border-radius: 8px; padding: 12px; }
                    .card strong { display: block; font-size: 20px; color: #111827; margin-top: 4px; }
                    table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                    th, td { border: 1px solid #d1d5db; padding: 10px; text-align: left; font-size: 12px; }
                    th { background: #f3f4f6; }
                </style>
            </head>
            <body>
                <h1>Relatório de acesso</h1>
                <p>Modo: ${escapeHtml(filters.view === "grouped" ? "Agrupado" : "Individual")}</p>
                <p>Período: ${escapeHtml(filters.startDate || "—")} até ${escapeHtml(filters.endDate || "—")}</p>
                <div class="summary">
                    <div class="card">Usuários<strong>${escapeHtml(summary.totalUsers)}</strong></div>
                    <div class="card">Sessões<strong>${escapeHtml(summary.totalSessions)}</strong></div>
                    <div class="card">Tempo total<strong>${escapeHtml(formatDuration(summary.totalDurationMs))}</strong></div>
                    <div class="card">Tempo médio<strong>${escapeHtml(formatDuration(summary.averageDurationMs))}</strong></div>
                </div>
                <table>
                    <thead>
                        <tr>${activeColumns.map((column) => `<th>${escapeHtml(column.label)}</th>`).join("")}</tr>
                    </thead>
                    <tbody>${rowsHtml}</tbody>
                </table>
                <script>
                    window.onload = function() {
                        window.print();
                    };
                </script>
            </body>
            </html>
        `);
        printWindow.document.close();
    };

    const showIndividualSelectionAlert = filters.view === "individual" && !filters.userId;

    return (
        <Box>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
                <Box bg="white" p={4} borderRadius="md" boxShadow="sm">
                    <Stat>
                        <StatLabel>Usuários no período</StatLabel>
                        <StatNumber>{summary.totalUsers || 0}</StatNumber>
                    </Stat>
                </Box>
                <Box bg="white" p={4} borderRadius="md" boxShadow="sm">
                    <Stat>
                        <StatLabel>Sessões</StatLabel>
                        <StatNumber color="blue.500">{summary.totalSessions || 0}</StatNumber>
                    </Stat>
                </Box>
                <Box bg="white" p={4} borderRadius="md" boxShadow="sm">
                    <Stat>
                        <StatLabel>Tempo total</StatLabel>
                        <StatNumber fontSize="xl" color="green.500">{formatDuration(summary.totalDurationMs)}</StatNumber>
                    </Stat>
                </Box>
                <Box bg="white" p={4} borderRadius="md" boxShadow="sm">
                    <Stat>
                        <StatLabel>Tempo médio</StatLabel>
                        <StatNumber fontSize="xl" color="purple.500">{formatDuration(summary.averageDurationMs)}</StatNumber>
                    </Stat>
                </Box>
            </SimpleGrid>

            <Box bg="white" p={4} borderRadius="md" boxShadow="sm" mb={4}>
                <HStack spacing={3} flexWrap="wrap">
                    <Input
                        type="date"
                        maxW="170px"
                        value={filters.startDate}
                        onChange={(e) => setFilters((current) => ({ ...current, startDate: e.target.value }))}
                    />
                    <Input
                        type="date"
                        maxW="170px"
                        value={filters.endDate}
                        onChange={(e) => setFilters((current) => ({ ...current, endDate: e.target.value }))}
                    />
                    <Select
                        maxW="180px"
                        value={filters.view}
                        onChange={(e) => setFilters((current) => ({ ...current, view: e.target.value }))}
                    >
                        <option value="grouped">Agrupado</option>
                        <option value="individual">Individual</option>
                    </Select>
                    <Select
                        maxW="260px"
                        value={filters.userId}
                        onChange={(e) => setFilters((current) => ({ ...current, userId: e.target.value }))}
                        placeholder={filters.view === "individual" ? "Selecione um usuário" : "Todos os usuários"}
                    >
                        {users.map((user) => (
                            <option key={user.id} value={user.id}>
                                {user.username}
                            </option>
                        ))}
                    </Select>
                    <Button colorScheme="teal" onClick={handleGenerateReport}>
                        Gerar relatório
                    </Button>
                    <Button onClick={handlePrint} isDisabled={sortedRows.length === 0}>
                        Imprimir
                    </Button>
                    <Tooltip label="Limpar filtros e recarregar">
                        <IconButton icon={<RepeatIcon />} onClick={handleReset} aria-label="Limpar relatório" />
                    </Tooltip>
                </HStack>
            </Box>

            {showIndividualSelectionAlert && (
                <Alert status="info" mb={4} borderRadius="md" bg="white">
                    <AlertIcon />
                    Selecione um usuário para visualizar o relatório individual.
                </Alert>
            )}

            <Box bg="white" borderRadius="md" boxShadow="sm" overflowX="auto">
                {loading ? (
                    <VStack py={12}>
                        <Spinner size="xl" color="teal.500" />
                        <Text color="gray.500">Gerando relatório...</Text>
                    </VStack>
                ) : (
                    <>
                        <Table variant="simple" size="sm">
                            <Thead bg="gray.50">
                                <Tr>
                                    {activeColumns.map((column) => {
                                        const isActiveSort = sortConfig.key === column.key;

                                        return (
                                            <Th key={column.key}>
                                                <Button
                                                    variant="unstyled"
                                                    display="inline-flex"
                                                    alignItems="center"
                                                    gap={1}
                                                    height="auto"
                                                    minW="auto"
                                                    fontSize="inherit"
                                                    fontWeight="inherit"
                                                    lineHeight="inherit"
                                                    textTransform="inherit"
                                                    letterSpacing="inherit"
                                                    color="inherit"
                                                    onClick={() => handleSort(column.key)}
                                                    _hover={{ color: "teal.600" }}
                                                >
                                                    <Text as="span">{column.label}</Text>
                                                    {isActiveSort ? (
                                                        sortConfig.direction === "asc" ? (
                                                            <TriangleUpIcon boxSize={2.5} />
                                                        ) : (
                                                            <TriangleDownIcon boxSize={2.5} />
                                                        )
                                                    ) : null}
                                                </Button>
                                            </Th>
                                        );
                                    })}
                                </Tr>
                            </Thead>
                            <Tbody>
                                {sortedRows.length === 0 ? (
                                    <Tr>
                                        <Td colSpan={filters.view === "grouped" ? 7 : 5} textAlign="center" py={8} color="gray.400">
                                            Nenhum resultado encontrado para o período informado
                                        </Td>
                                    </Tr>
                                ) : filters.view === "grouped" ? (
                                    sortedRows.map((row) => (
                                        <Tr key={row.userId || row.username} _hover={{ bg: "gray.50" }}>
                                            <Td>
                                                <VStack align="start" spacing={0}>
                                                    <Text fontWeight="medium" fontSize="sm">{row.username}</Text>
                                                    {row.userRole && (
                                                        <Text fontSize="xs" color="gray.500">Nível {row.userRole}</Text>
                                                    )}
                                                </VStack>
                                            </Td>
                                            <Td>{row.sessionsCount}</Td>
                                            <Td whiteSpace="nowrap" fontSize="xs">{formatDateTime(row.firstLoginAt)}</Td>
                                            <Td whiteSpace="nowrap" fontSize="xs">{formatDateTime(row.lastAuditAt)}</Td>
                                            <Td whiteSpace="nowrap" fontSize="xs">{formatDuration(row.totalDurationMs)}</Td>
                                            <Td whiteSpace="nowrap" fontSize="xs">{formatDuration(row.averageDurationMs)}</Td>
                                            <Td>
                                                <Text fontSize="xs" color="gray.600">{row.lastAction || "—"}</Text>
                                            </Td>
                                        </Tr>
                                    ))
                                ) : (
                                    sortedRows.map((row) => (
                                        <Tr key={row.id} _hover={{ bg: "gray.50" }}>
                                            <Td>
                                                <VStack align="start" spacing={0}>
                                                    <Text fontWeight="medium" fontSize="sm">{row.username}</Text>
                                                    {row.userRole && (
                                                        <Text fontSize="xs" color="gray.500">Nível {row.userRole}</Text>
                                                    )}
                                                </VStack>
                                            </Td>
                                            <Td whiteSpace="nowrap" fontSize="xs">{formatDateTime(row.loginAt)}</Td>
                                            <Td whiteSpace="nowrap" fontSize="xs">{formatDateTime(row.lastAuditAt)}</Td>
                                            <Td whiteSpace="nowrap" fontSize="xs">{formatDuration(row.durationMs)}</Td>
                                            <Td>
                                                <Text fontSize="xs" color="gray.600">{row.lastAction || "—"}</Text>
                                            </Td>
                                        </Tr>
                                    ))
                                )}
                            </Tbody>
                        </Table>

                        <Divider />
                        <HStack justify="space-between" p={3}>
                            <Text fontSize="sm" color="gray.500">
                                {sortedRows.length} registros exibidos
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                                Atualizado conforme os filtros do período pesquisado
                            </Text>
                        </HStack>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default AccessReportTab;